import React, { useState, useEffect } from 'react';

const Dashboard = () => {
    const [stats, setStats] = useState({
        totalTours: 0,
        totalBookings: 0,
        pendingBookings: 0,
        newContacts: 0
    });
    const [recentBookings, setRecentBookings] = useState([]);
    const [recentContacts, setRecentContacts] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadDashboard = React.useCallback(async () => {
        const token = localStorage.getItem('adminToken');

        try {
            // Load tours count
            const toursRes = await fetch('/api/tours?limit=1', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const toursData = await toursRes.json();

            // Load bookings
            const bookingsRes = await fetch('/api/bookings', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const bookings = await bookingsRes.json();

            // Load contacts
            const contactsRes = await fetch('/api/contacts', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const contacts = await contactsRes.json();

            // Calculate stats
            const pendingCount = bookings.filter(b => b.status === 'pending').length;
            const newContactsCount = contacts.filter(c => c.status === 'new').length;

            setStats({
                totalTours: toursData.pagination.total,
                totalBookings: bookings.length,
                pendingBookings: pendingCount,
                newContacts: newContactsCount
            });

            setRecentBookings(bookings.slice(0, 5));
            setRecentContacts(contacts.slice(0, 5));

        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Dashboard</h2>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow hover:transform hover:-translate-y-1 transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Tours</p>
                            <p className="text-3xl font-bold text-blue-600">{stats.totalTours}</p>
                        </div>
                        <i className="fas fa-map-marked-alt text-4xl text-blue-200"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow hover:transform hover:-translate-y-1 transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Total Bookings</p>
                            <p className="text-3xl font-bold text-green-600">{stats.totalBookings}</p>
                        </div>
                        <i className="fas fa-calendar-check text-4xl text-green-200"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow hover:transform hover:-translate-y-1 transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">Pending Bookings</p>
                            <p className="text-3xl font-bold text-yellow-600">{stats.pendingBookings}</p>
                        </div>
                        <i className="fas fa-clock text-4xl text-yellow-200"></i>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow hover:transform hover:-translate-y-1 transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm">New Contacts</p>
                            <p className="text-3xl font-bold text-purple-600">{stats.newContacts}</p>
                        </div>
                        <i className="fas fa-envelope text-4xl text-purple-200"></i>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-bold mb-4">Recent Bookings</h3>
                    <div className="space-y-3">
                        {recentBookings.length > 0 ? (
                            recentBookings.map((booking, index) => (
                                <div key={index} className="border-b pb-2">
                                    <p className="font-semibold">{booking.customer.name}</p>
                                    <p className="text-sm text-gray-600">{booking.tourName}</p>
                                    <p className="text-xs text-gray-500">
                                        {new Date(booking.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500">No recent bookings</p>
                        )}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-xl font-bold mb-4">Recent Contacts</h3>
                    <div className="space-y-3">
                        {recentContacts.length > 0 ? (
                            recentContacts.map((contact, index) => (
                                <div key={index} className="border-b pb-2">
                                    <p className="font-semibold">{contact.name}</p>
                                    <p className="text-sm text-gray-600">{contact.subject || 'No subject'}</p>
                                    <p className="text-xs text-gray-500">
                                        {new Date(contact.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500">No recent contacts</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
